import HR from '@/components/elements/HR/HR';
import Header from '@/components/elements/Header/Header';
import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import { Button, LinkButton } from '@/components/elements/forms/buttons';
import { ListItem, ListItemContent } from '@/components/elements/lists';
import BundleListItem from '@/components/elements/lists/BundleCardListItem/BundleCardListItem';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { InitialsAvatar, PersonAvatar } from '@/components/elements/redesign/Avatar';
import Icon from '@/components/icons/Icon';
import { IconVariant } from '@/components/icons/types';
import PageViewLayout from '@/components/layouts/PageViewLayout/PageViewLayout';
import CardWrapper from '@/components/modules/CardWrapper';
import AppointmentDetailsSummary from '@/components/modules/appointment/AppointmentDetailsSummary';
import AddToCalendarModal, { useAddToCalendarModal } from '@/components/modules/modals/AddToCalendar';
import Modal from '@/components/modules/modals/redesign/Modal/Modal';
import GoBack from '@/components/modules/pages/bokningar/GoBack';
import EmptyState from '@/components/templates/EmptyState';
import { SCREEN_NAME } from '@/constants/analytics';
import {
  ADD_TO_CALENDAR_ACTION_ITEM,
  APPOINTMENT_PAYMENT_METHOD,
  BOOK_AGAIN_ACTION_ITEM,
  CANCEL_BOOKING_ACTION_ITEM,
  CHANGE_BOOKING_TIME_ACTION_ITEM,
  MEETING_LINK_ACTION_ITEM,
  PAY_ONLINE_ACTION_ITEM,
} from '@/constants/appointmentConstants';
import {
  canPayCardOnFileAfterBooking,
  canPayKlarnaAfterBooking,
  canPayQliroAfterBooking,
  isGroupBooking as checkIfGroupBooking,
  classnames,
  getCanCancelAppointmentProps,
  getIsBookable,
  getUserInitials,
  isBookingInThePast,
  isSistaminuten,
  promiseWrapper,
} from '@/helpers';
import { getBundleListContentProps } from '@/helpers/bundle';
import { paymentActivationFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import { useAppSelector } from '@/hooks';
import { LoginContext } from '@/hooks/useLogin';
import useMobileView from '@/hooks/useMobileView';
import useTrackScreenShown, { trackScreenShown } from '@/hooks/useTrackScreenShown';
import { _s, _t } from '@/locale';
import { bookServices as oldBookServices } from '@/services/bookServices';
import { bookServices } from '@/services/bookServicesTs';
import { ConfirmedBooking } from '@/types/api/services/booking';
import { bookingSchema } from '@/types/api/services/booking/schema';
import { AppointmentActionIdentifier, AppointmentActionItem } from '@/types/appointment';
import { Dispatch, useContext, useEffect, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getVisibleGroupBookingParticipantFields,
  handleBookAgain,
  handleCancelAppointment,
} from './AppointmentDetails.helpers';
import {
  AppointmentDetailsAction,
  AppointmentDetailsState,
  GroupBookingParticipantInfo,
} from './AppointmentDetails.types';

const baseTranslationKey = 'pages.bokning.$id.AppointmentDetails';

const participantInfoMap: {
  [key in keyof GroupBookingParticipantInfo]: {
    icon: IconVariant;
    label: string;
    displayOrder: number;
  };
} = {
  givenName: { displayOrder: 1, icon: 'user', label: _s('participantInfo.givenName') },
  familyName: { displayOrder: 2, icon: 'user', label: _s('participantInfo.familyName') },
  email: { displayOrder: 3, icon: 'email', label: _s('participantInfo.email') },
  mobile: { displayOrder: 4, icon: 'phone', label: _s('participantInfo.mobile') },
  phone: { displayOrder: 5, icon: 'phone', label: _s('participantInfo.phone') },
  postalCode: { displayOrder: 6, icon: 'home', label: _s('participantInfo.postalCode') },
  streetAddress: { displayOrder: 7, icon: 'home', label: _s('participantInfo.streetAddress') },
  locality: { displayOrder: 8, icon: 'home', label: _s('participantInfo.locality') },
  message: { displayOrder: 9, icon: 'sms', label: _s('message') },
  cancellationCode: { displayOrder: 10, icon: 'calendar-cross', label: _s('participantInfo.cancellationCode') },
} as const;

const getScreenViewEvent = (appointment: ConfirmedBooking) => {
  return {
    screen_name: SCREEN_NAME.BOOKING_DETAILS,
    booking_id: appointment.id,
    company_id: appointment.place.id,
    customer_id: appointment.client,
  };
};

const AppointmentDetails = ({
  state,
  dispatch,
}: {
  state: AppointmentDetailsState;
  dispatch: Dispatch<AppointmentDetailsAction>;
}) => {
  const location = useLocation<{ from?: string; changedBookingTime?: boolean }>();
  const history = useHistory();
  const { appointment, submitting } = state;
  const [participantInfoModal, setParticipantInfoModal] = useState<GroupBookingParticipantInfo | null>(null);
  const { isMobileView } = useMobileView();

  const groupBookingProps = checkIfGroupBooking(appointment, true, true);
  const isGroupBooking = Boolean(groupBookingProps.groupBooking > 0);
  const appliedBundle = appointment.appliedBundle;

  const handleShowParticipantInfoModal = (participant: GroupBookingParticipantInfo) => {
    setParticipantInfoModal(participant);
    trackScreenShown({ screen_name: SCREEN_NAME.PARTICIPANT_DETAILS });
  };

  const handleCloseParticipantInfoModal = () => {
    setParticipantInfoModal(null);
    trackScreenShown(getScreenViewEvent(appointment));
  };

  const handleBackButtonClick = () => {
    if (isSistaminuten()) {
      history.push('/');
      return;
    }
    history.push(location?.state?.from ?? '/bokningar');
  };

  useTrackScreenShown(getScreenViewEvent(appointment));

  useEffect(() => {
    if (location.state?.changedBookingTime) {
      toast(({ closeToast }) => (
        <Snackbar
          onClose={closeToast}
          label={_s(`${baseTranslationKey}.snackbar.changeBookingTimeSuccess`)}
          type="success"
        />
      ));

      history.replace({
        pathname: location.pathname,
        state: { ...(location?.state ?? {}), changedBookingTime: false },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageViewLayout
      type="subView"
      title={_s(`${baseTranslationKey}.page-title`)}
      back
      onBackButtonClick={handleBackButtonClick}
      wrapperClass={isMobileView ? 'bg-brown-50' : 'bg-gradient'}>
      {submitting && <LoadingPlaceHolder className="fixed h-screen" />}
      <div className="lg:py-xxl lg:container">
        <div className="gap-xxl flex items-start">
          <div className="gap-lg flex grow-[9999] basis-[600px] flex-col">
            <CardWrapper className="bg-white">
              <AppointmentDetailsSummary appointment={appointment} />
            </CardWrapper>

            {appliedBundle && (
              <CardWrapper className="bg-white">
                <div className="py-lg pl-lg">
                  <Header label={_s(`${baseTranslationKey}.section.bundle.title`)} size="lg" />
                </div>

                <BundleListItem
                  {...getBundleListContentProps(appliedBundle)}
                  src={`/klippkortdetaljer/${appliedBundle.id}`}
                />
              </CardWrapper>
            )}

            {isGroupBooking && (
              <CardWrapper className="bg-white">
                <div className="py-lg pl-lg">
                  <Header label={_s(`${baseTranslationKey}.section.participants.title`)} size="lg" />
                </div>
                {groupBookingProps.personsInfo.map((participantInfo, key) => {
                  const title = getVisibleGroupBookingParticipantFields(participantInfo);
                  const subTitle = getVisibleGroupBookingParticipantFields(participantInfo, title);
                  const { givenName, familyName } = participantInfo;
                  const userInitials = givenName && familyName && getUserInitials({ about: { givenName, familyName } });

                  return (
                    <ListItem
                      key={key}
                      leftSlot={
                        userInitials ? (
                          <InitialsAvatar initials={userInitials} size="md" variant="default" />
                        ) : (
                          <PersonAvatar alt="" size="md" variant="default" />
                        )
                      }
                      onClick={() => handleShowParticipantInfoModal(participantInfo)}
                      rightSlot={<Icon variant="chevron-s-right" />}
                      underline>
                      <ListItemContent subTitle={subTitle} title={title} />
                    </ListItem>
                  );
                })}
              </CardWrapper>
            )}
            <CardWrapper className="w-full lg:hidden">
              <div className="p-lg empty:hidden">
                <AppointmentActions appointment={appointment} dispatch={dispatch} />
              </div>
            </CardWrapper>
          </div>
          <div className="sticky top-20 hidden lg:flex lg:grow-[100] lg:basis-[390px]">
            <CardWrapper className="w-full empty:hidden">
              <AppointmentActions appointment={appointment} dispatch={dispatch} />
            </CardWrapper>
          </div>
        </div>
      </div>
      <Modal isOpen={Boolean(participantInfoModal)}>
        <Modal.Content floating={!isMobileView}>
          <Modal.Header
            title={_s(`${baseTranslationKey}.modal.participant.header`)}
            onClose={handleCloseParticipantInfoModal}
          />
          {Object.entries(participantInfoModal || {})
            .map(([key, value]) => {
              const { icon, label, displayOrder } = participantInfoMap[key] || {};
              if (!icon || !label || !value) return null;
              return { icon, label, value, displayOrder };
            })
            .filter(Boolean)
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map(({ icon, label, value }, key) => (
              <ListItem key={label} leftSlot={<Icon variant={icon} />} underline>
                <ListItemContent content={value} title={label} />
              </ListItem>
            ))}
        </Modal.Content>
      </Modal>
    </PageViewLayout>
  );
};

const AppointmentActions = ({
  appointment,
  dispatch,
}: {
  appointment: ConfirmedBooking;
  dispatch: Dispatch<AppointmentDetailsAction>;
}) => {
  const {
    onOpen: handleOpenCalendarModal,
    onClose: onCloseCalendarModal,
    ...addToCalendarProps
  } = useAddToCalendarModal(appointment);
  const { isMobileView } = useMobileView();
  const appDispatch = useDispatch();
  const [showCancelAppointmentModal, setShowCancelAppointmentModal] = useState(false);
  const { user } = useAppSelector((state) => state.users);
  const isSistaminutentiderBooking = Boolean(isSistaminuten() || appointment.extra?.isSistaminutentiderBooking);
  const isPaidOffline = appointment.extra?.paymentMethod === APPOINTMENT_PAYMENT_METHOD.PAY_AT_PLACE;
  const canPayKlarna = canPayKlarnaAfterBooking(appointment);
  const canPayQliro = canPayQliroAfterBooking(appointment);
  const canPayCardOnFile = canPayCardOnFileAfterBooking(appointment);

  const showActivatePaymentCTA =
    (canPayKlarna || canPayQliro || canPayCardOnFile) && isPaidOffline && !isSistaminutentiderBooking;

  const meetingLink = appointment?.extra?.meetingLink;
  const showMeetingLink = meetingLink && meetingLink.length > 0;
  const fullMeetingLink = meetingLink && meetingLink.startsWith('https://') ? meetingLink : `https://${meetingLink}`;

  const isBookable = getIsBookable(appointment);
  const { canCancel: showCancelAppointmentCTA } = getCanCancelAppointmentProps(appointment);
  const showBookAgainCTA = (isBookable && isBookingInThePast(appointment)) || appointment.status !== 1;
  const showChangeAppointmentTimeCTA = showCancelAppointmentCTA && isBookable;

  const { handleClick: handleBookAgainClick, bookAgainUrl } = handleBookAgain(appointment, user, appDispatch);

  const handleCloseCalendarModal = () => {
    onCloseCalendarModal();
    trackScreenShown(getScreenViewEvent(appointment));
  };

  const handleOpenCancelAppointmentModal = () => {
    setShowCancelAppointmentModal(true);
    trackScreenShown({ screen_name: SCREEN_NAME.CANCEL_BOOKING, initiated_from: SCREEN_NAME.BOOKING_DETAILS });
  };

  const handleCloseCancelAppointmentModal = () => {
    setShowCancelAppointmentModal(false);
    trackScreenShown(getScreenViewEvent(appointment));
  };

  const handleConfirmCancelAppointmentClick = () => {
    handleCloseCancelAppointmentModal();
    handleCancelAppointment(appointment, user, appDispatch, dispatch);
  };

  const handleActionClick = (identifier: AppointmentActionIdentifier) => {
    switch (identifier) {
      case AppointmentActionIdentifier.MEETING_LINK:
        return;
      case AppointmentActionIdentifier.PAY_ONLINE:
        paymentActivationFlowTrackingSession.initiate(appointment.place, user, SCREEN_NAME.BOOKING_DETAILS, {
          booking_id: appointment.id,
        });
        return;
      case AppointmentActionIdentifier.CHANGE_BOOKING_TIME:
        handleBookAgainClick(true);
        return;
      case AppointmentActionIdentifier.ADD_TO_CALENDAR:
        handleOpenCalendarModal();
        return;
      case AppointmentActionIdentifier.BOOK_AGAIN:
        handleBookAgainClick();
        return;
      case AppointmentActionIdentifier.CANCEL_BOOKING:
        handleOpenCancelAppointmentModal();
        return null;
    }
  };

  const actions = (() => {
    const list: AppointmentActionItem[] = [
      ...(showMeetingLink ? [{ ...MEETING_LINK_ACTION_ITEM, to: { pathname: fullMeetingLink } }] : []),
      ...(showActivatePaymentCTA
        ? [{ ...PAY_ONLINE_ACTION_ITEM, to: { pathname: `/booking/checkout/${appointment.id}` } }]
        : []),
      // The calendar's "Avbryt" steps back onto this entry rather than stacking a second one, and
      // the booking id on the calendar's entry is the only thing that makes it a move.
      ...(showChangeAppointmentTimeCTA
        ? [
            {
              ...CHANGE_BOOKING_TIME_ACTION_ITEM,
              to: { pathname: bookAgainUrl, state: { fromLanding: true, changeTimeBookingId: appointment.id } },
            },
          ]
        : []),
      ...(showBookAgainCTA ? [{ ...BOOK_AGAIN_ACTION_ITEM, to: { pathname: bookAgainUrl } }] : []),
      ...(isBookable ? [{ ...ADD_TO_CALENDAR_ACTION_ITEM }] : []),
      ...(showCancelAppointmentCTA ? [{ ...CANCEL_BOOKING_ACTION_ITEM }] : []),
    ];

    return list;
  })();

  const handleCopyLink = (fullMeetingLink: string) => {
    navigator.clipboard.writeText(fullMeetingLink);

    toast(({ closeToast }) => <Snackbar type="success" label={_s(`videoMeetingLinkCopy`)} onClose={closeToast} />);
  };

  return (
    actions.length > 0 && (
      <div className="space-y-md flex w-full flex-col">
        {actions.map((action, key) => {
          const Action = action.to ? LinkButton : Button;
          const isFirstItem = key === 0;
          const variant = action.variant === 'link' ? 'link' : isFirstItem ? 'primary' : 'secondary';
          const iconProps = (() => {
            if (!action.icon) return null;
            return { variant: action.icon, color: action.iconColor };
          })();

          const isMeetingLink = action.identifier === AppointmentActionIdentifier.MEETING_LINK;

          return (
            <>
              <Action
                key={action.identifier}
                label={action.label}
                variant={variant}
                size="md"
                onClick={() => handleActionClick(action.identifier)}
                {...(action.to && { to: action.to })}
                {...(isMeetingLink && { target: '_blank' })}
                {...(iconProps && { leftIcon: <Icon {...iconProps} /> })}
              />
              {isMeetingLink && (
                <>
                  <p className="py-xs flex justify-center gap-2">
                    <span title={fullMeetingLink} className="text-fg_primary truncate">
                      {meetingLink}
                    </span>
                    <Icon variant="copy" className="cursor-pointer" onClick={() => handleCopyLink(fullMeetingLink)} />
                  </p>
                  <HR />
                </>
              )}
            </>
          );
        })}
        <AddToCalendarModal onClose={handleCloseCalendarModal} {...addToCalendarProps} />
        <Modal isOpen={showCancelAppointmentModal}>
          <Modal.Content floating={!isMobileView}>
            <Modal.Header
              title={_s(`${baseTranslationKey}.modal.cancel.header`)}
              onClose={handleCloseCancelAppointmentModal}
            />
            <div className={classnames('gap-xl flex flex-col', isMobileView && 'px-lg')}>
              <p>{_s(`${baseTranslationKey}.modal.cancel.body`)}</p>
              <div className="pt-lg flex flex-col">
                <Button onClick={handleCloseCancelAppointmentModal} variant="primary">
                  {_s(`${baseTranslationKey}.modal.cancel.cta.keepBooking`)}
                </Button>
                <Button onClick={handleConfirmCancelAppointmentClick} variant="link" className="text-danger-500">
                  {_s(`${baseTranslationKey}.modal.cancel.cta.cancelBooking`)}
                </Button>
              </div>
            </div>
          </Modal.Content>
        </Modal>
      </div>
    )
  );
};

async function fetchAndSetAppointmentDetails(id: number, dispatch: Dispatch<AppointmentDetailsAction>) {
  const getAppointmentDetails = isSistaminuten() ? oldBookServices.getSistaminutenBooking : bookServices.appointment;
  const { data, error } = await promiseWrapper(getAppointmentDetails(id));

  const validate = bookingSchema.safeParse(data?.booking);

  if (validate.success === false || error) {
    dispatch({ type: 'ERROR' });
    return;
  }

  dispatch({ type: 'SET_APPOINTMENT', payload: validate.data });
}

function appointmentReducer(state: AppointmentDetailsState, action: AppointmentDetailsAction) {
  switch (action.type) {
    case 'SET_APPOINTMENT':
      return {
        appointment: action.payload,
        loading: false,
        error: false,
        submitting: false,
      };
    case 'ERROR':
      return {
        ...state,
        loading: false,
        error: true,
        submitting: false,
      };
    case 'SUBMITTING':
      return {
        ...state,
        submitting: action.payload,
      };
  }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default function () {
  const routeMatch = useRouteMatch() as { params: { id: string } };
  const user = useAppSelector((state) => state.users)?.user;
  const { isMobileView } = useMobileView();
  const loginContext = useContext(LoginContext);
  const isLoggedInUser = Boolean(user && user.token) || isSistaminuten();

  const { id } = routeMatch.params;

  const [state, dispatch] = useReducer(appointmentReducer, {
    appointment: undefined,
    error: false,
    loading: true,
    submitting: false,
  });

  useEffect(() => {
    fetchAndSetAppointmentDetails(Number(id), dispatch);
  }, [id]);

  if (!isLoggedInUser) {
    return (
      <PageViewLayout
        type="subView"
        title={_s(`${baseTranslationKey}.page-title`)}
        back
        backSrc="/bokningar"
        wrapperClass={isMobileView ? 'bg-brown-50' : 'bg-gradient'}>
        <EmptyState
          title={_s(`${baseTranslationKey}.emptystate.title`)}
          body={_s(`${baseTranslationKey}.emptystate.body`)}
          icon={<img src="/images/illustrations/bookings2.png" alt="" />}
          cta={loginContext.renderLoginButton({
            label: _t(`${baseTranslationKey}.emptystate.cta`),
            size: 'sm',
            trackPromptEvent: true,
            initiatedFrom: 'direct_links',
          })}
        />
      </PageViewLayout>
    );
  }
  if (state.loading) return <LoadingPlaceHolder />;
  if (state.error) return <GoBack />;

  return <AppointmentDetails state={state} dispatch={dispatch} />;
}
